import { Button, useToast } from '@chakra-ui/react';
import React, { useState, useCallback } from 'react';
// eslint-disable-next-line @typescript-eslint/naming-convention
import { utils, writeFile, WorkBook, WorkSheet } from 'xlsx';

import { date as commonDate } from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';

import {
  ConcentrationLimitInsightsGqlDataGQLType,
  CoreBorrowingBaseInsightsGqlDataGQLType,
} from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import {
  ChartTimePeriod,
  ConcentrationLimitFormatMap,
  expandConcentrationLimitAggregateInsights,
  expandEligibilityKickoutsData,
  formatChartDataByReportingDate,
  formatConcentrationLimitDataForKey,
  formatCsvDownloadDataRows,
  getInitialActiveFields,
} from '../shared/portfolioChartUtils';
import { chartGroupMap } from './portfolioBorrowingBaseChartConfigs';
import { Icon } from '~/packages/common-frontend/components/icons';
import {
  errorToastConfig,
  getDefaultDashboardDisplayEndDate,
  getDefaultDashboardDisplayStartDate,
  successToastConfig,
} from '~/packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from '~/packages/common-frontend/utils';
import {
  useGetDailyCoreBorrowingBaseInsightsLazyQueryGQLType,
  useGetDailyEligibilityInsightsLazyQueryGQLType,
  useGetDailyConcentrationInsightsLazyQueryGQLType,
} from '~/packages/portfolio/requests/requests.gql.generated';

type DownloadAllChartsButtonProps = {
  creditFacilityId: string;
  concentrationLimitFormatMap: ConcentrationLimitFormatMap;
  creditAgreementConcentrationLimits: CreditAgreement.DomainEntity.ConcentrationLimitProperty[];
};

// Truncate sheet name to 31 characters (Excel limit)
const truncateSheetName = (name: string): string => {
  return name.length > 31 ? name.substring(0, 31) : name;
};

// Helper function to add a sheet to the workbook
const addSheetToWorkbook = (
  workbook: WorkBook,
  dataRows: { [key: string]: any }[],
  headers: { label: string; key: string }[],
  sheetName: string,
) => {
  // Create data array with headers as first row
  const headerRow = headers.map((h) => h.label);
  const data = [
    headerRow,
    ...dataRows.map((row) => headers.map((h) => row[h.key] ?? '')),
  ];

  const worksheet: WorkSheet = utils.aoa_to_sheet(data);
  utils.book_append_sheet(workbook, worksheet, sheetName);
};

// Helper function to get a unique sheet name (using recursion instead of while)
const getUniqueSheetName = (
  chartTitle: string,
  usedSheetNames: Set<string>,
  counter: number = 1,
): string => {
  const suffix = ` (${counter})`;
  const sheetName =
    counter === 1
      ? truncateSheetName(chartTitle)
      : truncateSheetName(
          `${chartTitle.substring(0, 31 - suffix.length)}${suffix}`,
        );

  if (!usedSheetNames.has(sheetName)) {
    usedSheetNames.add(sheetName);
    return sheetName;
  }

  return getUniqueSheetName(chartTitle, usedSheetNames, counter + 1);
};

export const DownloadAllChartsButton = ({
  creditFacilityId,
  concentrationLimitFormatMap,
  creditAgreementConcentrationLimits,
}: DownloadAllChartsButtonProps) => {
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const [
    fetchBorrowingBase,
  ] = useGetDailyCoreBorrowingBaseInsightsLazyQueryGQLType();
  const [fetchEligibility] = useGetDailyEligibilityInsightsLazyQueryGQLType();
  const [
    fetchConcentration,
  ] = useGetDailyConcentrationInsightsLazyQueryGQLType();

  const downloadAllCharts = useCallback(async () => {
    setIsLoading(true);

    try {
      const todayDateStr = commonDate.currentDateStr();
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const workbook = utils.book_new();

      const creditFacilityParams = {
        creditFacilityId,
        reportingDateStart: getDefaultDashboardDisplayStartDate(),
        reportingDateEnd: getDefaultDashboardDisplayEndDate(),
      };

      // Fetch all data in parallel
      const [
        borrowingBaseResult,
        eligibilityResult,
        concentrationResult,
      ] = await Promise.all([
        fetchBorrowingBase({ variables: { creditFacilityParams } }),
        fetchEligibility({ variables: { creditFacilityParams } }),
        fetchConcentration({ variables: { creditFacilityParams } }),
      ]);

      // Process borrowing base charts
      if (borrowingBaseResult.data?.getDailyCoreBorrowingBaseInsights) {
        const borrowingBaseData = formatChartDataByReportingDate({
          chartData: borrowingBaseResult.data.getDailyCoreBorrowingBaseInsights,
          selectedTimePeriod: ChartTimePeriod.day,
        });

        // Generate sheet for each borrowing base chart config
        chartGroupMap.borrowingBase.forEach((chartMap) => {
          const activeFields = getInitialActiveFields({
            chartConfig: chartMap.config,
          }) as (keyof CoreBorrowingBaseInsightsGqlDataGQLType)[];

          const { dataRows, headers } = formatCsvDownloadDataRows(
            borrowingBaseData,
            activeFields,
          );

          addSheetToWorkbook(
            workbook,
            dataRows,
            headers,
            truncateSheetName(chartMap.chartTitle),
          );
        });
      }

      // Process eligibility charts
      if (borrowingBaseResult.data?.getDailyCoreBorrowingBaseInsights) {
        // Eligible Exposure chart (from chartGroupMap.eligibility)
        const eligibilityBaseData = formatChartDataByReportingDate({
          chartData:
            borrowingBaseResult.data?.getDailyCoreBorrowingBaseInsights ?? [],
          selectedTimePeriod: ChartTimePeriod.day,
        });

        chartGroupMap.eligibility.forEach((chartMap) => {
          const activeFields = getInitialActiveFields({
            chartConfig: chartMap.config,
          }) as (keyof CoreBorrowingBaseInsightsGqlDataGQLType)[];

          const { dataRows, headers } = formatCsvDownloadDataRows(
            eligibilityBaseData,
            activeFields,
          );

          addSheetToWorkbook(
            workbook,
            dataRows,
            headers,
            truncateSheetName(chartMap.chartTitle),
          );
        });
      }

      // Eligibility Kickouts chart
      if (eligibilityResult.data?.getDailyEligibilityInsights) {
        const expandedEligibilityData = expandEligibilityKickoutsData(
          eligibilityResult.data.getDailyEligibilityInsights,
        );
        const formattedEligibilityData = formatChartDataByReportingDate({
          chartData: expandedEligibilityData,
          selectedTimePeriod: ChartTimePeriod.day,
        });

        const eligibilityKeys = Object.keys(formattedEligibilityData[0] ?? {});
        const {
          dataRows: eligibilityRows,
          headers: eligibilityHeaders,
        } = formatCsvDownloadDataRows(
          formattedEligibilityData,
          eligibilityKeys,
        );

        addSheetToWorkbook(
          workbook,
          eligibilityRows,
          eligibilityHeaders,
          truncateSheetName('Eligibility Kickouts'),
        );
      }

      // Process concentration charts
      if (concentrationResult.data?.getDailyConcentrationInsights) {
        // Excess Concentration Amounts chart
        const expandedConcentrationData = expandConcentrationLimitAggregateInsights(
          concentrationResult.data.getDailyConcentrationInsights,
        );
        const formattedConcentrationData = formatChartDataByReportingDate({
          chartData: expandedConcentrationData,
          selectedTimePeriod: ChartTimePeriod.day,
        });

        const concentrationKeys = Object.keys(
          formattedConcentrationData[0] ?? {},
        );
        const {
          dataRows: concentrationRows,
          headers: concentrationHeaders,
        } = formatCsvDownloadDataRows(
          formattedConcentrationData,
          concentrationKeys,
        );

        addSheetToWorkbook(
          workbook,
          concentrationRows,
          concentrationHeaders,
          truncateSheetName('Excess Concentration'),
        );

        // Individual concentration limit charts
        const usedSheetNames = new Set<string>();
        concentrationLimitFormatMap.forEach((_valueType, limitKey) => {
          const chartData = formatConcentrationLimitDataForKey(
            concentrationResult.data!.getDailyConcentrationInsights,
            limitKey,
          );

          const formattedChartData = formatChartDataByReportingDate({
            chartData,
            selectedTimePeriod: ChartTimePeriod.day,
          });

          const creditAgreementLimit = creditAgreementConcentrationLimits.find(
            (limit) =>
              limit._config_.concentrationLimitConfig?.propertyKey.toLowerCase() ===
              limitKey.toLowerCase(),
          );

          const chartTitle = creditAgreementLimit?.name ?? limitKey;
          const concentrationFields: (keyof ConcentrationLimitInsightsGqlDataGQLType)[] = [
            'reportingDate',
            'concentration',
            'concentrationAmount',
            'concentrationLimit',
            'totalConcentrationLimitKickout',
          ];

          const {
            dataRows: limitRows,
            headers: limitHeaders,
          } = formatCsvDownloadDataRows(
            formattedChartData,
            concentrationFields,
          );

          const sheetName = getUniqueSheetName(chartTitle, usedSheetNames);
          addSheetToWorkbook(workbook, limitRows, limitHeaders, sheetName);
        });
      }

      // Download the workbook
      writeFile(workbook, `borrowing_base_charts-${todayDateStr}.xlsx`);

      toast({
        ...successToastConfig,
        title: 'Charts downloaded successfully',
      });
    } catch (error: unknown) {
      toast({
        ...errorToastConfig,
        title: getUnexpectedErrorMessage(error as Error),
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    creditFacilityId,
    concentrationLimitFormatMap,
    creditAgreementConcentrationLimits,
    fetchBorrowingBase,
    fetchEligibility,
    fetchConcentration,
    toast,
  ]);

  return (
    <Button
      variant="secondary-muted"
      colorScheme="gray"
      leftIcon={<Icon icon="arrow-down-to-bracket" />}
      onClick={downloadAllCharts}
      isLoading={isLoading}
      loadingText="Downloading..."
    >
      Download All
    </Button>
  );
};
